import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Pager, SortTh, useLocalSort, istDT } from "../../components/common";

const TONE = { allow: "text-emerald-400", deny: "text-rose-400", blocked: "text-amber-400", alert: "text-rose-400" };

export default function AdminAudit() {
  const [f, setF] = useState({});
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], total: 0 });
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/admin/audit", { params: { ...f, page } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [f, page]);
  const verify = () => api.get("/admin/audit/verify").then((r) => setV(r.data)).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <PageHeader eyebrow="Governance" title="Audit log" desc="Records sign-ins, sensitive views, changes, approvals, exports, permission updates, denied attempts and blocked copy/print attempts. Each entry is hash-chained to the one before, so any edit or deletion breaks the chain.">
        <button data-testid="audit-verify-button" onClick={verify} className="border h-9 px-3 text-sm hover:border-primary">Verify chain integrity</button>
      </PageHeader>
      {v && <div data-testid="audit-verify-result" className={`border p-3 mb-4 text-sm flex items-center gap-2 ${v.valid ? "border-emerald-500/40 text-emerald-400" : "border-rose-500/40 text-rose-400"}`}>
        {v.valid ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}{v.valid ? `Chain intact · ${v.checked} entries verified` : `Chain broken at entry #${v.broken_at_seq}`}</div>}
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="audit-filter-action" placeholder="Action contains…" onKeyDown={(e) => e.key === "Enter" && (setPage(1), setF({ ...f, action: e.target.value || undefined }))} className="bg-muted/60 border h-9 px-2 text-sm" />
        <input data-testid="audit-filter-email" placeholder="User email…" onKeyDown={(e) => e.key === "Enter" && (setPage(1), setF({ ...f, email: e.target.value || undefined }))} className="bg-muted/60 border h-9 px-2 text-sm" />
        <select data-testid="audit-filter-outcome" onChange={(e) => { setPage(1); setF({ ...f, outcome: e.target.value || undefined }); }} className="bg-muted/60 border h-9 px-2 text-sm">
          <option value="">All outcomes</option>{["allow", "deny", "blocked", "alert"].map((o) => <option key={o}>{o}</option>)}</select>
      </div>
      <div className="ax-panel ax-scroll ax-freeze"><table className="ax-table w-full min-w-[1000px]" data-testid="audit-table">
        <AuditRows items={d.items} />
      </table></div>
      <Pager page={d.page || 1} total={d.total} size={50} onPage={setPage} />
    </div>
  );
}

function AuditRows({ items }) {
  const [sorted, sort] = useLocalSort(items, "seq", -1);
  return (
    <>
        <thead><tr>{[["seq", "#"], ["at", "Time (IST)"], ["email", "User"], ["action", "Action"], ["resource_type", "Resource"], ["outcome", "Outcome"], [null, "Detail"], ["ip", "IP"], [null, "Hash"]].map(([k, l]) => <SortTh key={l} k={k} label={l} sort={sort} />)}</tr></thead>
        <tbody>{sorted.map((e) => (
          <tr key={e.seq}><td className="num text-xs">{e.seq}</td><td className="num text-xs whitespace-nowrap">{istDT(e.at, true)}</td><td className="text-xs">{e.email || "system"}</td>
            <td className="font-mono text-xs">{e.action}</td><td className="font-mono text-[11px]">{e.resource_type ? `${e.resource_type}:${e.resource_id}` : "—"}</td>
            <td className={`text-xs ${TONE[e.outcome] || ""}`}>{e.outcome}</td><td className="font-mono text-[10px] max-w-[260px] truncate" title={JSON.stringify(e.detail)}>{JSON.stringify(e.detail)}</td>
            <td className="font-mono text-[10px]">{e.ip}</td><td className="font-mono text-[10px] text-muted-foreground">{e.hash.slice(0, 10)}</td></tr>
        ))}</tbody>
    </>
  );
}
